import React, { useMemo } from 'react';
import Tag from 'src/modules/shared/components/atoms/tag';
import Tooltip from 'src/modules/shared/components/atoms/tooltips/tooltip';
import type { TranslationKey } from 'src/modules/shared/types/i18n/translation-keys.type';
import type { IntegrationSyncMetadataItem } from 'src/modules/settings/components/molecules/integrations/integration-sync-metadata';

interface IntegrationSyncSummaryProps {
  items: IntegrationSyncMetadataItem[];
  t: (key: TranslationKey, params?: Record<string, string | number>) => string;
}

export const IntegrationSyncSummary: React.FC<IntegrationSyncSummaryProps> = ({
  items,
  t,
}) => {
  const viewItems = useMemo(() => {
    if (items.length === 0) return [];

    const compactValue = (value: string): string => {
      const match = value.match(/\(([^)]+)\)\s*$/);
      return match?.[1]?.trim() ?? value;
    };

    const resolveShortLabelKey = (labelKey: string): TranslationKey => {
      if (labelKey.includes('.lastError')) {
        return 'settings.integrations.syncSummary.lastError';
      }
      if (labelKey.includes('.pausedUntil')) {
        return 'settings.integrations.syncSummary.pausedUntil';
      }
      if (labelKey.includes('.lastSuccess')) {
        return 'settings.integrations.syncSummary.lastSuccess';
      }
      if (labelKey.includes('.lastAttempt')) {
        return 'settings.integrations.syncSummary.lastAttempt';
      }
      if (labelKey.includes('.next')) {
        return 'settings.integrations.syncSummary.next';
      }
      return 'settings.integrations.syncSummary.next';
    };

    const byKind = items.reduce(
      (acc, item) => {
        const key = item.labelKey;
        const kind = key.includes('.lastError')
          ? 'lastError'
          : key.includes('.pausedUntil')
            ? 'pausedUntil'
            : key.includes('.lastSuccess')
              ? 'lastSuccess'
              : key.includes('.lastAttempt')
                ? 'lastAttempt'
                : key.includes('.next')
                  ? 'next'
                  : 'other';

        acc[kind] = item;
        return acc;
      },
      {} as Record<string, IntegrationSyncMetadataItem | undefined>
    );

    const selected: IntegrationSyncMetadataItem[] = [];

    if (byKind.pausedUntil) {
      selected.push(byKind.pausedUntil);
    } else if (byKind.lastError) {
      selected.push(byKind.lastError);
    } else if (byKind.lastSuccess) {
      selected.push(byKind.lastSuccess);
    } else if (byKind.lastAttempt) {
      selected.push(byKind.lastAttempt);
    }

    if (byKind.next) {
      selected.push(byKind.next);
    }

    return selected.map((item) => ({
      ...item,
      compact: compactValue(item.value),
      shortLabelKey: resolveShortLabelKey(item.labelKey),
    }));
  }, [items]);

  if (viewItems.length === 0) return null;

  return (
    <div className="flex flex-wrap gap-2">
      {viewItems.map((item) => (
        <Tooltip
          key={item.labelKey}
          as="span"
          placement="top"
          size="xs"
          content={
            <div className="space-y-1">
              <div className="font-medium">{t(item.labelKey)}</div>
              <div>{item.value}</div>
            </div>
          }
        >
          <Tag
            variant="secondary"
            appearance="soft"
            size="xs"
            content={
              <span className="inline-flex items-center gap-1 min-w-0">
                <span className="sr-only">{t(item.labelKey)}</span>
                <span className="whitespace-nowrap">
                  {t(item.shortLabelKey)}
                </span>
                <span className="truncate max-w-[10rem]">{item.compact}</span>
              </span>
            }
            textClassName="min-w-0"
          />
        </Tooltip>
      ))}
    </div>
  );
};
